import type { AloftDate, SubjectData } from '@aloft/toolkit-shared';
import {
  MILES_TO_METERS,
  getDefaultRecommendedCompsGlaPercentage,
  getDefaultRecommendedCompsSiteSizePercentage,
} from '@aloft/toolkit-shared';

import { getLocationTypeProximityRequirementMiles } from '../../../utils/locationTypeProximityRequirementMiles.ts';
import { getLocationTypeSaleDateRequirementMonths } from '../../../utils/locationTypeSaleDateRequirementMonths.ts';
import { getPropertyTypeFilterValue } from '../internal/FilterPanel/propertyTypeOptions.ts';
import type { InitialCompSearch } from '../types.ts';

export const DEFAULT_RECOMMENDED_COMP_PROPERTY_SUB_TYPE = 'Single Family Residence';

export const getDefaultGlaPercentage = getDefaultRecommendedCompsGlaPercentage;
export const getDefaultSiteSizePercentage = getDefaultRecommendedCompsSiteSizePercentage;

export const getDefaultRecommendedCompSearch = (
  subject: SubjectData,
  _effectiveDate: AloftDate,
): InitialCompSearch['body']['search'] => {
  const { locationType, coordinates } = subject.editableData.propertyIdentification;
  const proximityRequirementMiles = getLocationTypeProximityRequirementMiles(locationType);
  const saleDateRequirementMonths = getLocationTypeSaleDateRequirementMonths(locationType) ?? 12;
  const propertySubtype = getPropertyTypeFilterValue(
    subject.editableData.improvements.propertyType,
  );

  return {
    filters: {
      status: '',
      propertySubType: propertySubtype
        ? [propertySubtype]
        : [DEFAULT_RECOMMENDED_COMP_PROPERTY_SUB_TYPE],
      saleDate: {
        variance: saleDateRequirementMonths,
      },
      salePrice: {},
      gla: {
        percentage: getDefaultGlaPercentage(locationType),
      },
      lotSize: {
        percentage: getDefaultSiteSizePercentage(locationType),
      },
      yearBuilt: {},
      bedrooms: {},
      fullBaths: {},
      halfBaths: {},
      stories: {
        variance: '',
      },
      garageSpaces: {},
      quality: {},
      condition: {},
      keywords: [],
    },
    geoSearch: {
      t: 'radius',
      radiusInMeters: (proximityRequirementMiles ?? 1) * MILES_TO_METERS,
      coord: [coordinates?.longitude ?? 0, coordinates?.latitude ?? 0],
    },
    matchCriteria: {
      saleDate: 'lasting_range',
      salePrice: 'min_max_range',
      gla: 'percentage_range',
      lotSize: 'percentage_range',
      yearBuilt: 'min_max_range',
      bedrooms: 'min_max_range',
      fullBaths: 'min_max_range',
      halfBaths: 'min_max_range',
      stories: 'value_range',
      garageSpaces: 'min_max_range',
      quality: 'min_max_range',
      condition: 'min_max_range',
    },
  } as unknown as InitialCompSearch['body']['search'];
};
